import React, { useEffect } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useViewSalesData } from "../hooks/useViewSalesData";
import { useSelector } from "react-redux";

const PrintInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  // Use the custom hook to fetch actual sale data
  const { sale, products, loading, error } = useViewSalesData(id);

  // Set document title for PDF filename
  useEffect(() => {
    document.title = `${isPartsURL? `Parts` : isAccessoriesURL? `Accessories` : `Mobile`}`;
    return () => {
      document.title = `${isPartsURL? `Parts` : isAccessoriesURL? `Accessories` : `Mobile`}`;
    };
  }, [isPartsURL, isAccessoriesURL]);

  // Show loading state
  if (loading) {
    return (
      <div className="print-invoice-container">
        <div className="loading-container">
          <p>Loading invoice data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !sale) {
    return (
      <div className="print-invoice-container">
        <div className="error-container">
          <p>Error loading invoice: {error || "Invoice not found"}</p>
          <button
            className="print-back-btn"
            onClick={() => navigate(`/sales/view/${id}`)}
          >
            <ArrowLeft size={15} />
            Back to Bill
          </button>
        </div>
      </div>
    );
  }

  // Transform products to match component expectations
  const transformedProducts =
    products?.map((product) => {
      const details = product.product_detail || {};
      let color = "";
      // const color = Array.isArray(details.variants)
      //   ? details.variants.find((v) => v.Color)?.Color || ""
      //   : "";

      if (product.variants && product.variants.length > 0) {
        const variantDetails = product.variants
          .map((variant) => Object.values(variant).filter(Boolean).join(", "))
          .filter(Boolean)
          .join(", ");
        if (variantDetails) {
          color = variantDetails;
        }
      }

      const ramStorage =
        details.ram && details.storage
          ? `${details.ram}/${details.storage}`
          : details.ram || details.storage || "";

      // Build display name (only add items that exist)
      const displayName = [
        details.name,
        details.suffix,
        ramStorage,
        color || "",
        details.used_mobile ? "Used" : "New",
      ]
        .filter(Boolean) // keep only non-empty values
        .join(" "); // join with a space
      return {
        id: product.id,
        name: details.name || product.name,
        displayName: displayName,
        quantity: product.qty || product.quantity || 0,
        unitPrice:
          parseFloat(
            product.effective_price ||
              product.price ||
              product.effectivePrice ||
              product.sellPrice
          ) || 0,
        subTotal: parseFloat(
          product.discounted_sub_total ||
            product.sub_total ||
            product.subTotal ||
            product.discountedSubTotal ||
            0
        ),
      };
    }) || [];

  // Calculate values from API data
  const shippingCost = parseFloat(sale.shippingAmount) || 0;
  const totalQty =
    sale.totalQty ||
    transformedProducts.reduce((sum, product) => sum + product.quantity, 0);
  // Use amount (total_amount) for product total, or calculate from products if not available
  const productTotal =
    parseFloat(sale.amount) ||
    transformedProducts.reduce((sum, product) => sum + product.subTotal, 0);
  const subTotal = productTotal + shippingCost;
  const previousBalance = parseFloat(sale.previousBalance) || 0;
  const paidBalance = parseFloat(sale.paidAmount) || 0;
  const resultingBalance = parseFloat(sale.resultingBalance);

  // Format date from createdAt
  const formattedDate = sale.createdAt
    ? new Date(sale.createdAt).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A";

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="print-invoice-container">
      {/* Header with Back and Print buttons */}
      <div className="print-invoice-header">
        <div>
          <button
            className={`${
              isPartsURL
                ? "print-back-btn-parts"
                : isAccessoriesURL
                ? "print-back-btn-acc"
                : "print-back-btn"
            }`}
            onClick={() => navigate(`/sales/view/${id}`)}
          >
            <ArrowLeft size={15} />
            Back to Bill
          </button>
        </div>

        <div>
          <button
            className={`${
              isPartsURL
                ? "print-print-btn-parts"
                : isAccessoriesURL
                ? "print-print-btn-acc"
                : "print-print-btn"
            }`}
            onClick={handlePrint}
          >
            <Printer size={16} />
            Print
          </button>
        </div>
      </div>

      {/* Invoice Content */}
      <div
        className={`${
          isPartsURL
            ? "print-invoice-content-parts"
            : isAccessoriesURL
            ? "print-invoice-content-acc"
            : "print-invoice-content"
        }`}
      >
        {/* Products Table */}
        <div className="print-products-table" style={{ border: "none" }}>
          <table className="print-table" style={{ border: "none" }}>
            <thead style={{ border: "none" }}>
              <tr className="print-header-info-row">
                <th
                  colSpan="4"
                  className="print-header-info-cell"
                  style={{ padding: "10px", border: "none" }}
                >
                  <div className="print-invoice-info">
                    <div className="print-invoice-id">{sale.invoiceId}</div>
                    <div className="print-invoice-date">{formattedDate}</div>
                  </div>
                  <div className="print-invoice-info-name">
                    <div className="print-customer-name">
                      Customer Name: {sale.customerName}
                    </div>
                  </div>
                </th>
              </tr>
              <tr style={{ border: "1px solid #e5e7eb" }}>
                <th>PRODUCT</th>
                <th style={{ textAlign: "center" }}>QTY.</th>
                <th style={{ textAlign: "center" }}>UNIT PRICE</th>
                <th style={{ textAlign: "center" }}>SUBTOTAL</th>
              </tr>
            </thead>
            <tbody style={{ border: "1px solid #e5e7eb" }}>
              {transformedProducts.map((product) => (
                <tr key={product.id}>
                  <td className="print-product-name">
                    {product.displayName || product.name}
                  </td>
                  <td className="print-quantity">{product.quantity}</td>
                  <td className="print-unit-price">
                    €{product.unitPrice.toFixed(2)}
                  </td>
                  <td className="print-subtotal">
                    €{product.subTotal.toFixed(2)}
                  </td>
                </tr>
              ))}
              <tr className="print-shipping-row">
                <td className="print-shipping-label">Shipping Cost</td>
                <td className="print-shipping-qty">-</td>
                <td className="print-shipping-price">
                  €{shippingCost.toFixed(2)}
                </td>
                <td className="print-shipping-total">
                  €{shippingCost.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Summary Section */}
        <div className="print-summary-section">
          <div className="print-balance-section"></div>
          <div className="print-summary-table">
            <table className="print-summary-table-inner">
              <thead>
                <tr>
                  <th>TOTAL QTY.</th>
                  <th>TOTAL UNIT PRICE</th>
                  <th>SUB TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="print-total-qty">{totalQty}</td>

                  <td className="print-total-unit-price">
                    €{productTotal.toFixed(2)}
                  </td>

                  {/* main Subtotal */}
                  <td className="print-total-subtotal">
                    €{subTotal.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td colSpan="2" className="print-total-unit-price">
                    PREVIOUS BALANCE
                  </td>
                  <td className="print-total-subtotal">
                    €{previousBalance.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td colSpan="2" className="print-total-unit-price">
                    PAID BALANCE
                  </td>
                  <td className="print-total-subtotal">
                    €{paidBalance.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan="2"
                    className={`print-total-unit-price ${
                      resultingBalance === 0
                        ? "print-total-unit-price-normal"
                        : resultingBalance < 0
                        ? "print-total-unit-price-green"
                        : "print-total-unit-price-red"
                    }`}
                  >
                    RESULTING BALANCE
                  </td>
                  <td
                    className={`print-total-unit-price ${
                      resultingBalance === 0
                        ? "print-total-unit-price-normal"
                        : resultingBalance < 0
                        ? "print-total-unit-price-green"
                        : "print-total-unit-price-red"
                    }`}
                  >
                    {resultingBalance === 0
                      ? `€${resultingBalance.toFixed(2)}`
                      : resultingBalance < 0
                      ? `€${(resultingBalance * -1).toFixed(2)}`
                      : `-€${resultingBalance.toFixed(2)}`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Warning text */}
        {isPartsURL || isAccessoriesURL ? null : (
          <div className="print-warranty-warning">
            <div
              className="print-warranty-title"
              style={{ color: "#000000", fontWeight: "600", fontSize: "12px" }}
            >
              ⚠ Wichtiger Garantiehinweis:
            </div>
            <ul
              className="print-warranty-list"
              style={{ color: "#000000", fontWeight: "600", fontSize: "12px" }}
            >
              <li>
                Asiatische Handys: Keine Garantie in Europa und auch
                nicht bei uns.
              </li>
              <li>
                Neue Europäische / Globale Handys: Hersteller Garantie - Für
                Garantieansprüche wenden Sie sich bitte direkt
                an den Hersteller.
              </li>
              <li>
                Gebrauchte Ware: 90 Tage Umtausch- bzw. Reparaturgarantie. Nach
                90 Tagen besteht keine Garantie.
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintInvoice;
